import type { DirectoryPrivate } from '@repo/types/directory';

import { useQueryClient } from '@tanstack/react-query';

import { useBatchMutation } from '#batch-mutations/useBatchMutations';
import { DirectoryKey } from '#directory/keys';

import { FileService } from './fileService';

export function useDeleteFiles(directoryId?: string) {
  const queryClient = useQueryClient();

  return useBatchMutation({
    mutationFn: (id: string) => FileService.deleteFile(id).then((response) => response.data),
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: [DirectoryKey.DIRECTORY, directoryId],
      });
      const prevDirectory = queryClient.getQueryData<DirectoryPrivate>([DirectoryKey.DIRECTORY, directoryId]);

      if (prevDirectory) {
        queryClient.setQueryData<DirectoryPrivate>([DirectoryKey.DIRECTORY, directoryId], (prev) => {
          if (!prev) {
            return;
          }
          return {
            ...prev,
            files: prev.files.filter((file) => file.id !== id),
          };
        });
      }

      return { prevDirectory };
    },
    onError: (_error, _variables, context) => {
      if (context?.prevDirectory) {
        queryClient.setQueryData([DirectoryKey.DIRECTORY, directoryId], context.prevDirectory);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [DirectoryKey.DIRECTORY, directoryId],
      });
    },
  });
}
